import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import PolicyForm from '../../components/PolicyForm';
import { AUTH_CONFIG } from '../../config';
import AdminLayout from '../../components/AdminLayout';

const AddPolicy = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  
  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Use the shared AUTH_CONFIG helper instead of direct fetch
        const isAuth = await AUTH_CONFIG.isAuthenticated();
        setIsAuthenticated(isAuth);
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthChecking(false);
      }
    };
    
    checkAuth();
  }, []);
  
  // Handle form submission
  const handleSubmit = async (policyData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await fetch('/api/policies/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(policyData)
      });
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        
        if (contentType.includes('text/html')) {
          throw new Error(`Server error ${response.status}: ${response.statusText}`);
        }
        
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to submit policy');
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Navigate to the policy edit page on success
        console.log(`Policy created successfully with ID: ${data.policy.id}`);
        console.log(`Navigating to: /admin/policies/${data.policy.id}/edit`);
        navigate(`/admin/policies/${data.policy.id}/edit`);
      } else {
        throw new Error(data.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error submitting policy:', error);
      setSubmitError(error.message || 'Failed to submit policy');
      setIsSubmitting(false);
    }
  };
  
  // Handle cancel
  const handleCancel = () => {
    navigate('/admin/policies');
  };
  
  // Content to render when authenticated
  const authContent = (
    <div className="container mx-auto px-4 max-w-5xl">
      <div className="mb-6">
        <Link to="/admin/policies" className="inline-flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to policy management
        </Link>
      </div>
      
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">
            Add New Policy
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Submit a government policy for Claude AI analysis
          </p>
        </div>
        
        <div className="px-6 py-6">
          {/* Submit error message */}
          {submitError && (
            <div className="p-4 mb-6 bg-red-50 border-l-4 border-red-500 text-red-700">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 mr-2" />
                <span>{submitError}</span>
              </div>
            </div>
          )}
          
          <PolicyForm 
            onSubmit={handleSubmit} 
            onCancel={handleCancel} 
            isLoading={isSubmitting}
            submitButtonText="Submit Policy for Analysis"
          />
        </div>
      </div>
    </div>
  );

  // Loading spinner while checking auth
  const loadingContent = (
    <div className="text-center py-8">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      <p className="mt-2 text-gray-600">Checking authentication...</p>
    </div>
  );

  // Not authenticated message
  const unauthContent = (
    <div className="py-8 bg-red-50 rounded-lg text-center">
      <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
      <h2 className="text-xl font-bold text-red-800 mb-2">Authentication Required</h2>
      <p className="text-red-600 mb-6">
        You must be logged in as an administrator to access this area.
      </p>
      <Link
        to="/admin"
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
      >
        Go to Admin Login
      </Link>
    </div>
  );
  
  return (
    <AdminLayout>
      {authChecking ? loadingContent : !isAuthenticated ? unauthContent : authContent}
    </AdminLayout>
  );
};

export default AddPolicy;